import { EntityType } from '@frontend/common';

/** Shareable detail stack param; separate from the legacy `details` param to avoid double-opens. */
export const PLATFORM_DETAILS_PARAM = 'platform_details';

/**
 * One pane in the `?platform_details=[...]` stack. Everything a pane needs to restore from a pasted URL
 * lives on its own entry, so panes never share parents, tabs or context.
 */
export interface PlatformDetailEntry {
    type: EntityType | string;
    id: string;
    /** Parent ids for nested fetches (e.g. SLOT → spot_id, module_id). */
    parents?: Record<string, string>;
    /** Opener context that changes how the pane loads (e.g. application, product variant). */
    context?: Record<string, string>;
    tab?: string;
    dropdown?: string;
}

export function platformDetailKey(entry: Pick<PlatformDetailEntry, 'type' | 'id'>): string {
    return `${entry.type}:${entry.id}`;
}

function toStringRecord(value: unknown): Record<string, string> | undefined {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return undefined;
    }
    const entries = Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].length > 0);
    return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function toNonEmptyString(value: unknown): string | undefined {
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Builds a normalized entry (empty fields dropped so the URL stays short). Returns null without type or id. */
export function createPlatformDetailEntry(value: unknown): PlatformDetailEntry | null {
    if (!value || typeof value !== 'object') {
        return null;
    }
    const raw = value as Record<string, unknown>;
    const type = toNonEmptyString(raw['type']);
    const id = toNonEmptyString(raw['id']);
    if (!type || !id) {
        return null;
    }

    const entry: PlatformDetailEntry = { type, id };
    const parents = toStringRecord(raw['parents']);
    const context = toStringRecord(raw['context']);
    const tab = toNonEmptyString(raw['tab']);
    const dropdown = toNonEmptyString(raw['dropdown']);
    if (parents) entry.parents = parents;
    if (context) entry.context = context;
    if (tab) entry.tab = tab;
    if (dropdown) entry.dropdown = dropdown;
    return entry;
}

/** Parses the stack; invalid entries are dropped and duplicates keep their last (top-most) position. */
export function parsePlatformDetailStack(search: URLSearchParams | string): PlatformDetailEntry[] {
    const params = typeof search === 'string' ? new URLSearchParams(search) : search;
    const raw = params.get(PLATFORM_DETAILS_PARAM);
    if (!raw) {
        return [];
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return [];
    }
    if (!Array.isArray(parsed)) {
        return [];
    }

    return parsed.reduce<PlatformDetailEntry[]>((stack, value) => {
        const entry = createPlatformDetailEntry(value);
        return entry ? pushPlatformDetailEntry(stack, entry) : stack;
    }, []);
}

export function writePlatformDetailStack(search: URLSearchParams, stack: PlatformDetailEntry[]): void {
    if (stack.length === 0) {
        search.delete(PLATFORM_DETAILS_PARAM);
    } else {
        search.set(PLATFORM_DETAILS_PARAM, JSON.stringify(stack));
    }
}

/** Moves (or adds) the entry to the top of the stack. */
export function pushPlatformDetailEntry(stack: PlatformDetailEntry[], entry: PlatformDetailEntry): PlatformDetailEntry[] {
    const key = platformDetailKey(entry);
    return [...stack.filter((existing) => platformDetailKey(existing) !== key), entry];
}

export function removePlatformDetailEntry(stack: PlatformDetailEntry[], key: string): PlatformDetailEntry[] {
    return stack.filter((entry) => platformDetailKey(entry) !== key);
}

export function updatePlatformDetailTab(stack: PlatformDetailEntry[], key: string, tab: string, dropdown?: string): PlatformDetailEntry[] {
    return stack.map((entry) => {
        if (platformDetailKey(entry) !== key) {
            return entry;
        }
        return createPlatformDetailEntry({ ...entry, tab, dropdown }) ?? entry;
    });
}
